import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../api/client";
import { keys, useInvalidate } from "../api/hooks";
import { adminsResponseSchema, grantAdminResponseSchema } from "../../api/contracts";
import { formatInstant } from "../lib/format";
import { Badge, Banner, Button, Dialog, ErrorNote, Field, Input, PageHeader, Panel, Spinner } from "../components/ui";
import { DataTable } from "../components/DataTable";

export function AdminsPage() {
  const invalidate = useInvalidate();
  const list = useQuery({ queryKey: keys.admin.admins, queryFn: () => api("/api/admin/admins", adminsResponseSchema) });
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const grant = useMutation({
    mutationFn: (e: string) => api("/api/admin/admins", grantAdminResponseSchema, { method: "POST", body: { email: e } }),
    onMutate: () => setNote(null),
    onSuccess: (r) => { setNote(r.granted ? null : "That email is already a super admin (set in Railway)."); if (r.granted) setEmail(""); void invalidate(keys.admin.admins); },
  });
  const revoke = useMutation({
    mutationFn: (e: string) => api(`/api/admin/admins/${encodeURIComponent(e)}`, z.undefined(), { method: "DELETE" }),
    onSuccess: () => { setRevoking(null); void invalidate(keys.admin.admins); },
  });

  if (list.isPending) return <Spinner />;
  if (list.isError) return <ErrorNote error={list.error} />;
  const { superAdmins, admins, candidates } = list.data;
  const who = (a: { email: string; name: string | null }) => <><span className="font-medium">{a.email}</span>{a.name && <span className="text-hs-text-muted"> · {a.name}</span>}</>;

  return (
    <>
      <PageHeader title="Admins" subtitle="Admins can use every screen in this dashboard. Only super admins can change who is an admin." />
      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Super admins" padding="none">
          <DataTable rows={superAdmins} rowKey={(a) => a.email} density="compact" caption="Super admins" columns={[
            { key: "who", header: "Account", cell: (a) => who(a) },
            { key: "source", header: "", headerLabel: "Source", align: "right", width: "9rem", cell: () => <Badge tone="primary">Set in Railway</Badge> },
          ]} />
        </Panel>
        <Panel title="Grant admin">
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (email.trim()) grant.mutate(email); }}>
            <Field label="Email" className="min-w-56 flex-1"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@hellosugar.salon" /></Field>
            <Button type="submit" disabled={grant.isPending || !email.trim()}>Grant admin</Button>
          </form>
          <p className="mt-2 text-xs text-hs-text-muted">Works before they have signed in; it applies the next time they load the dashboard.</p>
          {note && <div className="mt-2"><Banner tone="info">{note}</Banner></div>}
          <div className="mt-2 empty:hidden"><ErrorNote error={grant.error} /></div>
        </Panel>
      </div>

      <Panel className="mt-4" title="Admins" padding="none">
        {admins.length === 0 ? <p className="p-4 text-[13px] text-hs-text-muted">No granted admins yet.</p> : (
          <DataTable rows={admins} rowKey={(a) => a.email} density="compact" caption="Granted admins" columns={[
            { key: "who", header: "Account", cell: (a) => <div>{who(a)}<p className="text-xs text-hs-text-muted">Granted by {a.grantedBy} {formatInstant(a.grantedAt)}</p></div> },
            { key: "revoke", header: "", headerLabel: "Actions", align: "right", width: "6rem", cell: (a) => <Button variant="ghost" size="sm" aria-label={`Revoke ${a.email}`} onClick={() => setRevoking(a.email)}>Revoke</Button> },
          ]} />
        )}
      </Panel>

      <Panel className="mt-4" title="Signed-in users" padding="none">
        {candidates.length === 0 ? <p className="p-4 text-[13px] text-hs-text-muted">Everyone who has signed in is already an admin.</p> : (
          <DataTable rows={candidates} rowKey={(a) => a.email} density="compact" caption="Signed-in users" columns={[
            { key: "who", header: "Account", cell: (a) => <div>{who(a)}{a.lastLoginAt && <p className="text-xs text-hs-text-muted">Last signed in {formatInstant(a.lastLoginAt)}</p>}</div> },
            { key: "make", header: "", headerLabel: "Actions", align: "right", width: "8rem", cell: (a) => <Button variant="secondary" size="sm" aria-label={`Make ${a.email} admin`} disabled={grant.isPending} onClick={() => grant.mutate(a.email)}>Make admin</Button> },
          ]} />
        )}
      </Panel>

      <Dialog open={revoking !== null} title={`Revoke admin for ${revoking}?`} onClose={() => setRevoking(null)}
        footer={<><Button variant="secondary" disabled={revoke.isPending} onClick={() => setRevoking(null)}>Cancel</Button><Button variant="danger" disabled={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking)}>Yes, revoke</Button></>}>
        <p>They lose the admin dashboard on their next page load. If they are linked to an owner group they keep their owner view.</p>
        <ErrorNote error={revoke.error} />
      </Dialog>
    </>
  );
}
